import "./style.scss";

import { Tooltip } from "@mui/material";
import classNames from "classnames";
import type { CSSProperties, ReactNode } from "react";
import { forwardRef } from "react";

import type { StyledButtonType } from "../StyledButton";
import StyledButton from "../StyledButton";

export interface ActionCardAction {
    icon?: ReactNode;
    label: string;
    onClick: () => void;
    /**
     * Type as in StyledButton prop. Default is "primary"
     */
    buttonType?: StyledButtonType;
    sx?: CSSProperties;
    tooltip: string;
}

export interface ActionCardProps {
    /**
     * Main title of the card
     */
    title: string;
    /**
     * Little description to show under the title
     */
    description: string | JSX.Element | undefined;
    /**
     * Optionnal icon to show on the left of the title
     */
    icon?: ReactNode;
    /**
     * Optionnal action
     * ```ts
     * {
     *    icon?: ReactNode
     *    label: string;
     *    onClick: () => void;
     * }
     * ```
     */
    action?: ActionCardAction | ActionCardAction[];
    /**
     * Optionnal style, using React.CSSProperties type
     */
    style?: CSSProperties;
    /**
     * Lessens the padding and adds grey border.
     */
    isCompact?: boolean;
    /**
     * Adds gradient border to card.
     */
    usesGradient?: boolean;
    /**
     * Uses square icon button.
     */
    squareBtn?: boolean;
    titleStyle?: CSSProperties;
    /**
     * Triggers function in parent component while passing action that was clicked on.
     */
    handleClick?: (action: ActionCardAction) => void;
    gradientAnimation?: boolean;
}

/**
 * Simple card with an optionnal action
 */
const ActionCard = forwardRef<any, ActionCardProps>(function ActionCard(props: ActionCardProps, ref) {
    const {
        title,
        description,
        icon,
        action,
        style,
        isCompact = false,
        usesGradient = false,
        squareBtn = false,
        titleStyle,
        handleClick,
        gradientAnimation,
    } = props;

    return (
        <div
            className={classNames({
                "action-card": true,
                compact: isCompact,
                gradient: usesGradient,
                "success-gradient": gradientAnimation,
            })}
            style={style}
            ref={ref}
        >
            <div className="action-card-main">
                <div className="action-card-head">
                    {icon && <div className="action-card-icon">{icon}</div>}
                    <div className="action-card-title" style={titleStyle}>
                        {title}
                    </div>
                </div>
                {action && <div className="action-card-description">{description}</div>}
            </div>
            {action ? (
                <div
                    className={classNames({
                        "action-card-action": true,
                        "thick-button": squareBtn,
                    })}
                >
                    {action instanceof Array
                        ? action.map((item, index) => (
                              <Tooltip title={item.tooltip} arrow key={`action-${index}`}>
                                  <div>
                                      <StyledButton
                                          className={classNames({
                                              "small-btn-icon": !isCompact && !item.label,
                                          })}
                                          sx={item.sx}
                                          buttonType={item.buttonType ? item.buttonType : "primary"}
                                          onClick={handleClick ? () => handleClick(item) : item.onClick}
                                      >
                                          {item.icon}
                                          {item.label}
                                      </StyledButton>
                                  </div>
                              </Tooltip>
                          ))
                        : action && (
                              <Tooltip title={action.tooltip} arrow>
                                  <StyledButton
                                      buttonType="primary"
                                      onClick={handleClick ? () => handleClick(action) : action.onClick}
                                  >
                                      {action.icon}
                                      {action.label}
                                  </StyledButton>
                              </Tooltip>
                          )}
                </div>
            ) : (
                <div className="action-card-description">{description}</div>
            )}
        </div>
    );
});

export default ActionCard;
